import * as React from "react";
import { useState, useRef, useEffect, useMemo } from "react";
import { Pagination } from "./pagination";
import { getAlignmentClass, getJustifyClass } from "./table-helper";
import { cn } from "../../lib/utils";

const Table = ({
  columns = [],
  data: paginatedData = [],
  stickyColumns = [],
  rowKey = "id",
  onRowClick,
  className = "",
  onSort,
  sortConfig = null,
  totalLoanCount = 0,
  currentPage = 1,
  pageSize = 5,
  handlePageChange
}) => {
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollRight, setScrollRight] = useState(0);
  const tableRef = useRef(null);
  const containerRef = useRef(null);
  // Calculate pagination values
  const totalPages = Math.ceil(totalLoanCount / pageSize);
  const shouldShowPagination = totalLoanCount > pageSize;

  // Handle scroll to show/hide sticky column shadows
  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
        setScrollLeft(scrollLeft);
        setScrollRight(scrollWidth - clientWidth - scrollLeft);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      handleScroll(); // Initial call
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Get sticky columns sorted by their position in the table
  const leftStickyColumns = useMemo(() => {
    return stickyColumns
      .map(colKey => ({
        key: colKey,
        index: columns.findIndex(c => c.key === colKey)
      }))
      .filter(col => col.index !== -1 && col.index < columns.length / 2)
      .sort((a, b) => a.index - b.index);
  }, [stickyColumns, columns]);

  const rightStickyColumns = useMemo(() => {
    return stickyColumns
      .map(colKey => ({
        key: colKey,
        index: columns.findIndex(c => c.key === colKey)
      }))
      .filter(col => col.index !== -1 && col.index >= columns.length / 2)
      .sort((a, b) => b.index - a.index);
  }, [stickyColumns, columns]);

  // Calculate sticky column positions
  const getLeftOffset = (columnKey) => {
    const columnIndex = columns.findIndex(c => c.key === columnKey);
    let offset = 0;

    for (const stickyCol of leftStickyColumns) {
      if (stickyCol.index >= columnIndex) break;
      const column = columns.find(c => c.key === stickyCol.key);
      offset += column?.width || 150;
    }

    return offset;
  };

  const getRightOffset = (columnKey) => {
    const columnIndex = columns.findIndex(c => c.key === columnKey);
    let offset = 0;

    for (const stickyCol of rightStickyColumns) {
      if (stickyCol.index <= columnIndex) break;
      const column = columns.find(c => c.key === stickyCol.key);
      offset += column?.width || 150;
    }

    return offset;
  };

  const isLeftSticky = (columnKey) => {
    return leftStickyColumns.some(col => col.key === columnKey);
  };

  const isRightSticky = (columnKey) => {
    return rightStickyColumns.some(col => col.key === columnKey);
  };

  const getStickyStyle = (column) => {
    if (isLeftSticky(column.key)) {
      return {
        position: "sticky",
        left: `${getLeftOffset(column.key)}px`,
        zIndex: 10,
      };
    }
    if (isRightSticky(column.key)) {
      return {
        position: "sticky",
        right: `${getRightOffset(column.key)}px`,
        zIndex: 10,
      };
    }
    return {};
  };

  const getShadowClass = (column) => {
    const isLeft = isLeftSticky(column.key);
    const isRight = isRightSticky(column.key);

    if (isLeft) {
      // Find if this is the rightmost left sticky column
      const isRightmostLeftSticky = leftStickyColumns.length > 0 && 
        leftStickyColumns[leftStickyColumns.length - 1].key === column.key;

      if (isRightmostLeftSticky) {
        // Shadow on the RIGHT side of left sticky column (inset from right)
        return "shadow-[inset_-2px_0_0px_rgba(0,0,0,0.15)]";
      }
    }

    if (isRight) {
      // Find if this is the leftmost right sticky column
      const isLeftmostRightSticky = rightStickyColumns.length > 0 && 
        rightStickyColumns[rightStickyColumns.length - 1].key === column.key;

      if (isLeftmostRightSticky) {
        // Shadow on the LEFT side of right sticky column (inset from left)
        return "shadow-[inset_2px_0_0px_rgba(0,0,0,0.15)]";
      }
    }

    return "";
  };

  const handleSort = (columnKey) => {
    if (onSort) {
      const direction =
        sortConfig?.key === columnKey && sortConfig?.direction === "asc"
          ? "desc"
          : "asc";
      onSort({ key: columnKey, direction });
    }
  };

  const renderCell = (column, row, rowIndex) => {
    if (column.renderCell) {
      return column.renderCell(row, rowIndex);
    }
    return row[column.key] || "";
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        ref={containerRef}
        className="overflow-auto border border-neutral-5 rounded-lg bg-white"
        style={{ maxHeight: "600px" }}
      >
        <table ref={tableRef} className="w-full border-collapse table-fixed">
          <thead>
            <tr className="">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    "px-5 py-3 text-sm tracking-wide text-neutral-8 border-b border-neutral-5 bg-primary-5 font-medium",
                    getShadowClass(column),
                    getAlignmentClass(column)
                  )}
                  style={{
                    ...getStickyStyle(column),
                    width: column.width,
                    minWidth: column.minWidth,
                  }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div
                    className={`flex items-center gap-2 ${getJustifyClass(
                      column
                    )}`}
                  >
                    {column.label}
                    {column.sortable && sortConfig?.key === column.key && (
                      <span className="text-[#A67C52]">
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={row[rowKey] || rowIndex}
                className={`
                  border-b border-[#F0F0F0] transition-colors
                  ${onRowClick ? "cursor-pointer hover:bg-[#F9F9F9]" : ""}
                `}
                onClick={() => onRowClick && onRowClick(row, rowIndex)}
                style={{ height: "80px" }}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`
                      px-5 py-3 text-sm align-middle font-light bg-white
                      ${getShadowClass(column)}
                      ${getAlignmentClass(column)}
                    `}
                    style={{
                      width: column.width,
                      minWidth: column.width,
                      ...getStickyStyle(column),
                    }}
                  >
                    {renderCell(column, row, rowIndex)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {shouldShowPagination && (
        <div className="flex justify-center mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            showPrevNext={true}
          />
        </div>
      )}
    </div>
  );
};

export { Table };
